import { useState, useCallback, useEffect, useRef } from 'react';

function getFullscreenElement(): Element | null {
  return (
    document.fullscreenElement ??
    (document as any).webkitFullscreenElement ??
    (document as any).mozFullScreenElement ??
    (document as any).msFullscreenElement ??
    null
  );
}

function isFullscreenSupported(): boolean {
  return !!(
    document.documentElement.requestFullscreen ??
    (document.documentElement as any).webkitRequestFullscreen ??
    (document.documentElement as any).mozRequestFullScreen ??
    (document.documentElement as any).msRequestFullscreen
  );
}

async function requestFullscreen(el: HTMLElement): Promise<void> {
  if (el.requestFullscreen) return el.requestFullscreen();
  if ((el as any).webkitRequestFullscreen) return (el as any).webkitRequestFullscreen();
  if ((el as any).mozRequestFullScreen) return (el as any).mozRequestFullScreen();
  if ((el as any).msRequestFullscreen) return (el as any).msRequestFullscreen();
  throw new Error('Fullscreen API not supported');
}

async function exitFullscreen(): Promise<void> {
  if (document.exitFullscreen) return document.exitFullscreen();
  if ((document as any).webkitExitFullscreen) return (document as any).webkitExitFullscreen();
  if ((document as any).mozCancelFullScreen) return (document as any).mozCancelFullScreen();
  if ((document as any).msExitFullscreen) return (document as any).msExitFullscreen();
}

export interface UseFullscreenReturn {
  isFullscreen: boolean;
  isSupported: boolean;
  enter: (el: HTMLElement) => Promise<boolean>;
  exit: () => Promise<void>;
  toggle: (el: HTMLElement) => Promise<boolean>;
}

export function useFullscreen(onExit?: () => void): UseFullscreenReturn {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;
  const isSupported = isFullscreenSupported();

  useEffect(() => {
    const handler = () => {
      const active = getFullscreenElement() != null;
      setIsFullscreen(active);
      if (!active) onExitRef.current?.();
    };
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    document.addEventListener('mozfullscreenchange', handler);
    document.addEventListener('MSFullscreenChange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
      document.removeEventListener('mozfullscreenchange', handler);
      document.removeEventListener('MSFullscreenChange', handler);
    };
  }, []);

  const enter = useCallback(async (el: HTMLElement): Promise<boolean> => {
    try {
      await requestFullscreen(el);
      return true;
    } catch {
      return false;
    }
  }, []);

  const exitFn = useCallback(async () => {
    if (getFullscreenElement()) {
      await exitFullscreen().catch(() => {});
    }
  }, []);

  const toggle = useCallback(async (el: HTMLElement): Promise<boolean> => {
    if (getFullscreenElement()) {
      await exitFullscreen().catch(() => {});
      return false;
    }
    return enter(el);
  }, [enter]);

  return { isFullscreen, isSupported, enter, exit: exitFn, toggle };
}
